import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../../../firebaseConfig';
import { doc, setDoc, serverTimestamp, getDocs, getDoc, collection } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { toast } from 'react-toastify';
import hash from 'object-hash';
import mapListRaw from '../../../data/mapList';
import { fetchActiveSeasonDoc } from '../../../utils/seasonApi';
import {
  getTeamAssetPackName,
  getTeamLogoUrl,
  listCommunityAssetPacks,
  migrateLegacySelectedAssetPack,
  normalizeCommunityAssetPlayers,
} from '../../../services/communityAssetPacks';
import {
  useAssignmentsDrag,
  usePlayerAssignments
} from '../../../components/StrategyRoom/PlayerAssignments';
import { useSequenceManager } from './useSequenceManager';

const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const sanitizeForFirestore = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeForFirestore(item))
      .filter((item) => item !== undefined);
  }

  if (value instanceof Date) return value;
  if (value?.toDate && typeof value.toDate === 'function') return value;

  if (typeof value === 'object') {
    const cleaned = {};
    Object.entries(value).forEach(([key, val]) => {
      const sanitized = sanitizeForFirestore(val);
      if (sanitized !== undefined) cleaned[key] = sanitized;
    });
    return cleaned;
  }

  return value;
};

const findPlayerNameByAvatar = (avatarUrl, players = []) => {
  if (!avatarUrl) return 'Unknown';
  for (const player of players) {
    if (player?.avatar === avatarUrl) {
      return player?.name || 'Unknown';
    }
  }
  return 'Unknown';
};

const normalizeStrategyCommunityPack = (pack = {}, idx = 0) => {
  const id =
    pack.id ||
    pack.sourceLibraryId ||
    pack.sourceTeam ||
    `legacy-selected-${idx + 1}`;

  return {
    id: String(id),
    sourceLibraryId: pack.sourceLibraryId || pack.id || null,
    sourceTeam: pack.sourceTeam || pack.teamId || null,
    sourceTeamName: pack.sourceTeamName || getTeamAssetPackName(pack),
    logo: getTeamLogoUrl(pack),
    players: normalizeStrategyCommunityPlayers(pack),
    isLegacySelected: Boolean(pack.isLegacySelected),
  };
};

const getRawCommunityPlayerList = (pack = {}) => {
  if (Array.isArray(pack.players) && pack.players.length > 0) return pack.players;
  if (Array.isArray(pack.roster) && pack.roster.length > 0) return pack.roster;
  if (Array.isArray(pack.playerPics) && pack.playerPics.length > 0) {
    return pack.playerPics.map((pic) => ({ avatar: pic }));
  }
  return [];
};

const normalizeIdentitySegment = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');

const normalizeStrategyCommunityPlayers = (pack = {}) => {
  const rawPlayers = getRawCommunityPlayerList(pack);
  const packId = String(
    pack.id ||
    pack.sourceLibraryId ||
    pack.sourceTeam ||
    'community-pack'
  );
  const sourceTeamName = pack.sourceTeamName || getTeamAssetPackName(pack);

  return normalizeCommunityAssetPlayers(pack).map((player, idx) => {
    const rawPlayer = rawPlayers[idx] || {};
    const playerId = rawPlayer.id || player.id || null;
    const name = player?.name || rawPlayer?.ign || rawPlayer?.name || `Player ${idx + 1}`;
    const identitySegment = playerId || normalizeIdentitySegment(name) || idx;

    return {
      id: playerId,
      name,
      avatar:
        player?.avatar ||
        rawPlayer?.avatar ||
        rawPlayer?.image ||
        rawPlayer?.pic ||
        rawPlayer?.url ||
        '',
      side: rawPlayer.side || player.side,
      source: 'community',
      sourcePackId: packId,
      sourceTeam: pack.sourceTeam || pack.teamId || null,
      sourceTeamName,
      sourcePlayerId: playerId,
      sourcePlayerIdentity: `${packId}:${identitySegment}`,
    };
  });
};

export const useStrategyLogic = (props) => {
  const navigate = useNavigate();
  const { strategyId } = useParams();
  const isViewer = props?.isViewerMode || false;

  // ============================================
  // STATE
  // ============================================
  const [showDropdown, setShowDropdown] = useState(false);
  const [isConfirmSaveModalOpen, setIsConfirmSaveModalOpen] = useState(false);
  const [communityAssetPacks, setCommunityAssetPacks] = useState([]);
  const [seasonAssets, setSeasonAssets] = useState(null);
  const [customPlayers, setCustomPlayers] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [teamId, setTeamId] = useState(null);
  const [activeSeasonId, setActiveSeasonId] = useState(null);
  const [activeSeasonYear, setActiveSeasonYear] = useState(null);
  const [teamAssetsExpanded, setTeamAssetsExpanded] = useState(false);
  const [teamAssetsSide, setTeamAssetsSide] = useState('ally');
  const [hoveredPlayerMenu, setHoveredPlayerMenu] = useState(null);
  const [strategyTitle, setStrategyTitle] = useState('');
  const [editingStrategyId, setEditingStrategyId] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [pendingMapName, setPendingMapName] = useState(null);
  const [isLoadedFromFirestore, setIsLoadedFromFirestore] = useState(false);
  const [saveNotification, setSaveNotification] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [teamLogo, setTeamLogo] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [mode, setMode] = useState('hardpoint');
  const [mapName, setMapName] = useState('');
  const [isMapCollapsed, setIsMapCollapsed] = useState(true);
  const [showAssignmentsOverlay, setShowAssignmentsOverlay] = useState(false);
  const [draggingUtilityId, setDraggingUtilityId] = useState(null);
  const [isPenPanelVisible, setIsPenPanelVisible] = useState(false);
  const [playersOnCanvas, setPlayersOnCanvas] = useState([]);
  const [utilitiesOnCanvas, setUtilitiesOnCanvas] = useState([]);
  const [textBoxes, setTextBoxes] = useState([]);
  const [drawnLines, setDrawnLines] = useState([]);
  const [imagesOnCanvas, setImagesOnCanvas] = useState([]);
  const [visionCones, setVisionCones] = useState([]);
  const [selectedConeType, setSelectedConeType] = useState('medium');
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 1000, height: 600 });
  const [draggingPlayerId, setDraggingPlayerId] = useState(null);
  const [isErasing, setIsErasing] = useState(false);
  const [draggingTextId, setDraggingTextId] = useState(null);
  const [draggingImageId, setDraggingImageId] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [penSettings, setPenSettings] = useState({
    type: 'line',
    color: '#ffffff',
    opacity: 10,
    thickness: 5,
  });
  const [lastTool, setLastTool] = useState('pen');
  const [isDrawing, setIsDrawing] = useState(false);
  const [isCursorInside, setIsCursorInside] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);

  // ============================================
  // REFS
  // ============================================
  const canvasLoadedRef = useRef(false);
  const hoverMenuTimeout = useRef(null);
  const dragJustEndedRef = useRef(false);
  const resizeObservers = useRef({});
  const penPanelRef = useRef(null);
  const canvasSize = useRef({ width: 1000, height: 600 });
  const dragOffset = useRef({ x: 0, y: 0 });
  const cursorRef = useRef(null);
  const pathRef = useRef(null);
  const pointsRef = useRef([]);
  const eraseThrottleRef = useRef(0);
  const lastEraserPointRef = useRef(null);
  const resizingRef = useRef({ id: null, side: null });
  const strategyBasePathRef = useRef({ basePath: null, seasonId: null, isLegacy: false });

  // ============================================
  // COMPUTED VALUES
  // ============================================
  const MODES = ['hardpoint', 'snd', 'control'];
  const modeLabels = { hardpoint: 'HP', snd: 'S&D', control: 'CTL' };
  const modeMaps = useMemo(() => mapListRaw[mode] || [], [mode]);
  const selectedMap = useMemo(
    () => modeMaps.find((m) => m.name === mapName) || {},
    [modeMaps, mapName]
  );
  const seasonPlayers = useMemo(
    () => seasonAssets?.players || [],
    [seasonAssets]
  );

  const allCommunityPlayers = useMemo(() => (
    Array.isArray(communityAssetPacks) ? communityAssetPacks : []
  ).flatMap((pack) =>
    normalizeStrategyCommunityPlayers(pack).filter(p => !!p.avatar)
  ), [communityAssetPacks]);

  const currentRoster = useMemo(() => {
    const source = seasonPlayers.length ? seasonPlayers : allCommunityPlayers;
    return source.map((p) => ({
      name: p?.name || p?.ign || 'PLAYER',
      avatar: p?.avatar || p?.image || p?.pic || p?.url || '',
    })).filter(p => !!p.avatar);
  }, [seasonPlayers, allCommunityPlayers]);
  const getPlayerNameByAvatar = (avatarUrl) =>
    findPlayerNameByAvatar(avatarUrl, [...seasonPlayers, ...allCommunityPlayers]);
  const primaryIconStyle = { width: '24px', height: '24px', objectFit: 'contain' };
  const secondaryIconStyle = { width: '20px', height: '20px', objectFit: 'contain' };

  const getSeasonStrategyBasePath = (targetTeamId, seasonId) =>
    `teams/${targetTeamId}/seasons/${seasonId}/strategies`;
  const getLegacyStrategyBasePath = (targetTeamId) => `teams/${targetTeamId}/strategies`;

  const resolveActiveSeasonId = async (targetTeamId) => {
    try {
      const { seasonId, year } = await fetchActiveSeasonDoc(targetTeamId);
      const resolvedId = seasonId ? String(seasonId) : null;
      const resolvedYear = year ? String(year) : null;
      if (resolvedId && resolvedId !== activeSeasonId) {
        setActiveSeasonId(resolvedId);
      }
      if (resolvedYear && resolvedYear !== activeSeasonYear) {
        setActiveSeasonYear(resolvedYear);
      }
      return resolvedId;
    } catch (err) {
      console.error('Failed to resolve active season', err);
      return null;
    }
  };

  const resolveStrategyBasePathForLoad = async (targetTeamId, targetStrategyId) => {
    const seasonId = activeSeasonId || await resolveActiveSeasonId(targetTeamId);
    const seasonBasePath = seasonId ? getSeasonStrategyBasePath(targetTeamId, seasonId) : null;

    if (seasonBasePath) {
      const seasonSnap = await getDoc(doc(db, `${seasonBasePath}/${targetStrategyId}`));
      if (seasonSnap.exists()) {
        return { basePath: seasonBasePath, seasonId, docSnap: seasonSnap, isLegacy: false };
      }
    }

    const legacyBasePath = getLegacyStrategyBasePath(targetTeamId);
    const legacySnap = await getDoc(doc(db, `${legacyBasePath}/${targetStrategyId}`));
    if (legacySnap.exists()) {
      return { basePath: legacyBasePath, seasonId: null, docSnap: legacySnap, isLegacy: true };
    }

    return {
      basePath: seasonBasePath || legacyBasePath,
      seasonId,
      docSnap: null,
      isLegacy: false,
    };
  };

  const resolveStrategyBasePathForWrite = async (targetTeamId) => {
    const seasonId = String(
      activeSeasonId ||
      await resolveActiveSeasonId(targetTeamId) ||
      new Date().getFullYear()
    );
    return {
      basePath: getSeasonStrategyBasePath(targetTeamId, seasonId),
      seasonId,
      isLegacy: false
    };
  };

  // ============================================
  // SEQUENCE MANAGER
  // ============================================
  const {
    activeStep,
    scenarioData,
    justLoaded,
    stepSwitching,
    loadedStepsLocked,
    setActiveStep,
    setScenarioData,
    setJustLoaded,
    setStepSwitching,
    setLoadedStepsLocked,
    handleStepSwitch,
    historyRef,
    suspendHistoryRef,
  } = useSequenceManager({
    playersOnCanvas,
    utilitiesOnCanvas,
    textBoxes,
    drawnLines,
    imagesOnCanvas,
    visionCones,
    setPlayersOnCanvas,
    setUtilitiesOnCanvas,
    setTextBoxes,
    setDrawnLines,
    setImagesOnCanvas,
    setVisionCones,
    isLoadedFromFirestore,
    canvasLoadedRef,
    draggingPlayerId,
    draggingUtilityId,
    draggingTextId,
    draggingImageId,
  });

  // ============================================
  // ASSIGNMENTS
  // ============================================
  const { assignmentRows, roleCounts, updateAssignment } = usePlayerAssignments(currentRoster, 10);
  const { assignPanelPos, handleDragStart: handleAssignDragStart } = useAssignmentsDrag({ x: 16, y: 16 });

  // ============================================
  // EFFECTS
  // ============================================

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

      if (ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }

      if (ctrlKey && e.key === 'Z' && e.shiftKey) {
        e.preventDefault();
        redo();
      }

      if (ctrlKey && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeStep, playersOnCanvas, utilitiesOnCanvas, textBoxes, drawnLines, imagesOnCanvas, visionCones]);

  // Auth check
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => {
      if (user) {
        setCurrentUser(user);
      } else {
        navigate('/login');
      }
    });
    return () => unsub();
  }, [navigate]);

  // Fetch user profile + team logo
  useEffect(() => {
    const fetchUserProfile = async () => {
      const user = auth?.currentUser;
      if (!user) return;

      const userRef = doc(db, `users/${user.uid}`);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserProfile(data);
        setTeamId(data.teamId || null);

        if (data.teamId) {
          const teamRef = doc(db, `teams/${data.teamId}`);
          const teamSnap = await getDoc(teamRef);
          if (teamSnap.exists()) {
            setTeamLogo(teamSnap.data().logo);
          }
        }
      }
    };

    fetchUserProfile();
  }, []);

  // Resolve active season for season-aware strategy paths
  useEffect(() => {
    let isMounted = true;
    if (!teamId) {
      setActiveSeasonId(null);
      return () => { isMounted = false; };
    }

    fetchActiveSeasonDoc(teamId)
      .then(({ seasonId, year }) => {
        if (isMounted) {
          setActiveSeasonId(seasonId ? String(seasonId) : null);
          setActiveSeasonYear(year ? String(year) : null);
        }
      })
      .catch((err) => {
        console.error('Failed to load active season', err);
        if (isMounted) {
          setActiveSeasonId(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [teamId]);

  // Load community asset packs, with legacy single-pack fallback.
  useEffect(() => {
    const loadAssets = async () => {
      if (!teamId) {
        setCommunityAssetPacks([]);
        return;
      }

      setAssetsLoading(true);
      try {
        const packs = await listCommunityAssetPacks(teamId);

        if (packs.length > 0) {
          const normalizedPacks = packs.map(normalizeStrategyCommunityPack);
          setCommunityAssetPacks(normalizedPacks);
          return;
        }

        const migration = await migrateLegacySelectedAssetPack(
          teamId,
          auth.currentUser?.uid || null
        );
        if (migration.pack) {
          const legacyPack = normalizeStrategyCommunityPack({
            ...migration.pack,
            isLegacySelected: true,
          });
          setCommunityAssetPacks([legacyPack]);
        } else {
          setCommunityAssetPacks([]);
        }
      } catch (err) {
        console.error('Failed to load community asset packs', err);
        setCommunityAssetPacks([]);
      } finally {
        setAssetsLoading(false);
      }
    };
    loadAssets();
  }, [teamId]);

  // Load season-specific assets (active players + logo) from configurations/stats
  useEffect(() => {
    const loadSeasonAssets = async () => {
      if (!teamId) return;
      try {
        const { getCurrentSeason } = await import('../../../data/seasons');
        const currentSeason = activeSeasonYear || getCurrentSeason();
        const configRef = doc(db, `teams/${teamId}/configurations/stats`);
        const snap = await getDoc(configRef);

        if (!snap.exists()) {
          setSeasonAssets(null);
          return;
        }

        const data = snap.data();
        const seasonConfig = data.seasons?.[currentSeason] || {};
        const playerList = Array.isArray(seasonConfig.players) ? seasonConfig.players : [];
        const activePlayers = playerList
          .filter(p => p?.isActive !== false)
          .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
          .map((p, idx) => ({
            name: (p.ign || p.name || `Player ${idx + 1}`).toUpperCase(),
            avatar: p.imageUrl || p.image || '',
            side: p.side,
            source: 'season'
          }));

        setSeasonAssets({
          logo: seasonConfig.teamLogo || data.teamLogo || null,
          players: activePlayers,
        });
      } catch (err) {
        console.error('Failed to load season assets', err);
        setSeasonAssets(null);
      }
    };

    loadSeasonAssets();
  }, [teamId, activeSeasonYear]);

  // Process season roster plus team-aware community players.
  useEffect(() => {
    const seasonPlayers = seasonAssets?.players || [];

    if (allCommunityPlayers.length === 0 && seasonPlayers.length === 0) {
      setCustomPlayers([]);
      return;
    }

    const merged = [];
    const seen = new Set();
    const pushUnique = (player, source = 'season') => {
      if (!player) return;
      const key =
        source === 'community'
          ? player.sourcePlayerIdentity || `${player.sourcePackId || 'community'}:${normalizeIdentitySegment(player.name)}`
          : `season:${normalizeIdentitySegment(player.name)}`;

      if (key && seen.has(key)) return;
      merged.push(player);
      if (key) seen.add(key);
    };

    seasonPlayers.forEach((player) => pushUnique(player, 'season'));
    allCommunityPlayers.forEach((player) => pushUnique(player, 'community'));

    setCustomPlayers(merged);
  }, [allCommunityPlayers, seasonAssets]);

  // Load strategy from Firestore
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      const userDoc = await getDoc(doc(db, `users/${user.uid}`));
      const targetTeamId = userDoc.data()?.teamId;
      if (!targetTeamId) return;

      const resolved = await resolveStrategyBasePathForLoad(targetTeamId, strategyId);
      strategyBasePathRef.current = {
        basePath: resolved.basePath,
        seasonId: resolved.seasonId,
        isLegacy: resolved.isLegacy,
      };

      if (resolved.docSnap?.exists()) {
        const stratData = resolved.docSnap.data();
        setStrategyTitle(stratData.name || '');
        if (stratData.mode) {
          setMode(stratData.mode);
        }
        if (stratData.map) {
          setPendingMapName(stratData.map);
        }
      }
    });

    return () => unsubscribe();
  }, [strategyId]);

  // Load strategy sequences
  useEffect(() => {
    const loadStrategyFromFirestore = async () => {
      if (!strategyId) return;

      const user = auth?.currentUser;
      if (!user) return;

      const userRef = doc(db, `users/${user.uid}`);
      const userSnap = await getDoc(userRef);
      const targetTeamId = userSnap.data()?.teamId;
      if (!targetTeamId) return;

      let basePath = strategyBasePathRef.current?.basePath;
      if (!basePath) {
        const resolved = await resolveStrategyBasePathForLoad(targetTeamId, strategyId);
        basePath = resolved.basePath;
        strategyBasePathRef.current = {
          basePath: resolved.basePath,
          seasonId: resolved.seasonId,
          isLegacy: resolved.isLegacy,
        };
      }

      const stepCollectionRef = collection(db, `${basePath}/${strategyId}/sequences`);
      const snapshot = await getDocs(stepCollectionRef);

      const loadedSteps = {};
      snapshot.forEach(doc => {
        const stepNum = parseInt(doc.id);
        loadedSteps[stepNum] = {
          ...doc.data(),
          stepId: stepNum,
        };
      });

      Object.entries(loadedSteps).forEach(([step, data]) => {
        loadedSteps[step] = {
          stepId: Number(step),
          playersOnCanvas: data.playerPositions || data.playersOnCanvas || [],
          utilitiesOnCanvas: data.utilityPlacements || data.utilitiesOnCanvas || [],
          drawnLines: data.drawings || data.drawnLines || [],
          textBoxes: data.textBoxes || [],
          imagesOnCanvas: data.imagesOnCanvas || [],
          visionCones: data.visionCones || []
        };
      });

      for (let i = 1; i <= 10; i++) {
        if (!loadedSteps[i]) {
          loadedSteps[i] = {
            stepId: i,
            playersOnCanvas: [],
            utilitiesOnCanvas: [],
            drawnLines: [],
            textBoxes: [],
            imagesOnCanvas: [],
            visionCones: [],
          };
        }
      }

      setScenarioData(loadedSteps);
      setActiveStep(1);
      setJustLoaded(true);
      setIsLoadedFromFirestore(true);

      const stepOne = loadedSteps[1];
      if (stepOne) {
        setPlayersOnCanvas(stepOne.playersOnCanvas);
        setUtilitiesOnCanvas(stepOne.utilitiesOnCanvas);
        setDrawnLines(stepOne.drawnLines);
        setTextBoxes(stepOne.textBoxes);
        setImagesOnCanvas(stepOne.imagesOnCanvas);
        setVisionCones(stepOne.visionCones || []);
      }

      setEditingStrategyId(strategyId);
    };

    loadStrategyFromFirestore();
  }, [strategyId]);

  // Canvas size update
  useEffect(() => {
    const updateSize = () => {
      const canvas = document.querySelector('.map-canvas');
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        canvasSize.current = {
          width: rect.width,
          height: rect.height,
          top: rect.top,
          left: rect.left
        };

        setCanvasDimensions({
          width: rect.width,
          height: rect.height
        });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Map name from mode maps
  useEffect(() => {
    setMapName(modeMaps[0]?.name || '');
  }, [modeMaps]);

  // Handle pending map name
  useEffect(() => {
    if (!pendingMapName) return;

    const found = modeMaps.find(m => m.name === pendingMapName);
    if (found) {
      setMapName(pendingMapName);
      setPendingMapName(null);
    }
  }, [modeMaps, pendingMapName]);

  const getClientPoint = (event) => {
    if (event?.touches?.length) {
      return {
        clientX: event.touches[0].clientX,
        clientY: event.touches[0].clientY,
      };
    }

    if (event?.changedTouches?.length) {
      return {
        clientX: event.changedTouches[0].clientX,
        clientY: event.changedTouches[0].clientY,
      };
    }

    if (typeof event?.clientX === 'number' && typeof event?.clientY === 'number') {
      return { clientX: event.clientX, clientY: event.clientY };
    }

    return null;
  };

  // Drag operations
  useEffect(() => {
    const handlePointerMove = (e) => {
      const point = getClientPoint(e);
      if (!point) return;

      const { width: canvasWidth, height: canvasHeight, top, left } = canvasSize.current;
      const hasActiveDrag =
        draggingTextId !== null ||
        draggingUtilityId !== null ||
        draggingPlayerId !== null ||
        draggingImageId !== null;

      if (hasActiveDrag && e.cancelable) {
        e.preventDefault();
      }

      if (draggingTextId !== null) {
        let x = point.clientX - left - dragOffset.current.x;
        let y = point.clientY - top - dragOffset.current.y;

        const boxWidth = textBoxes.find(tb => tb.id === draggingTextId)?.width || 140;
        const boxHeight = 32;

        x = Math.max(0, Math.min(x, canvasWidth - boxWidth));
        y = Math.max(0, Math.min(y, canvasHeight - boxHeight));

        const relX = (x / canvasWidth) * 100;
        const relY = (y / canvasHeight) * 100;

        setTextBoxes(prev =>
          prev.map(tb => tb.id === draggingTextId ? { ...tb, relX, relY } : tb)
        );
      }

      if (draggingUtilityId !== null) {
        let x = point.clientX - left - dragOffset.current.x;
        let y = point.clientY - top - dragOffset.current.y;

        x = Math.max(0, Math.min(x, canvasWidth - 45));
        y = Math.max(0, Math.min(y, canvasHeight - 45));

        const relX = (x / canvasWidth) * 100;
        const relY = (y / canvasHeight) * 100;

        setUtilitiesOnCanvas(prev =>
          prev.map(util =>
            util.id === draggingUtilityId ? { ...util, relX, relY } : util
          )
        );
      }

      if (draggingPlayerId !== null) {
        let x = point.clientX - left - dragOffset.current.x;
        let y = point.clientY - top - dragOffset.current.y;

        x = Math.max(0, Math.min(x, canvasWidth - 30));
        y = Math.max(0, Math.min(y, canvasHeight - 30));

        const relX = (x / canvasWidth) * 100;
        const relY = (y / canvasHeight) * 100;

        setPlayersOnCanvas(prev =>
          prev.map(p =>
            p.id === draggingPlayerId ? { ...p, relX, relY } : p
          )
        );
      }

      if (draggingImageId !== null) {
        setImagesOnCanvas(prev =>
          prev.map((img) => {
            if (img.id !== draggingImageId) return img;

            const imageWidth = img.width || 150;
            const imageHeight = img.height || 150;
            let x = point.clientX - left - dragOffset.current.x;
            let y = point.clientY - top - dragOffset.current.y;

            x = Math.max(0, Math.min(x, canvasWidth - imageWidth));
            y = Math.max(0, Math.min(y, canvasHeight - imageHeight));

            const relX = (x / canvasWidth) * 100;
            const relY = (y / canvasHeight) * 100;
            return { ...img, relX, relY };
          })
        );
      }
    };

    const handlePointerUp = () => {
      setDraggingTextId(null);
      setDraggingUtilityId(null);
      setDraggingPlayerId(null);
      setDraggingImageId(null);
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchend', handlePointerUp);
    window.addEventListener('touchcancel', handlePointerUp);
    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchend', handlePointerUp);
      window.removeEventListener('touchcancel', handlePointerUp);
    };
  }, [draggingTextId, draggingUtilityId, draggingPlayerId, draggingImageId, textBoxes]);

  // Cleanup resize observers
  useEffect(() => {
    return () => {
      Object.values(resizeObservers.current).forEach(observer => observer.disconnect());
    };
  }, []);

  // ============================================
  // FUNCTIONS
  // ============================================

  const undo = () => {
    const h = historyRef.current[activeStep];
    if (h.past.length === 0) return;

    suspendHistoryRef.current = true;

    const current = {
      playersOnCanvas,
      utilitiesOnCanvas,
      textBoxes,
      drawnLines,
      imagesOnCanvas,
      visionCones,
    };
    h.future.push(current);

    const prev = h.past.pop();
    if (prev) {
      setPlayersOnCanvas(prev.playersOnCanvas);
      setUtilitiesOnCanvas(prev.utilitiesOnCanvas);
      setTextBoxes(prev.textBoxes);
      setDrawnLines(prev.drawnLines);
      setImagesOnCanvas(prev.imagesOnCanvas);
      setVisionCones(prev.visionCones || []);
    }

    setTimeout(() => {
      suspendHistoryRef.current = false;
    }, 50);
  };

  const redo = () => {
    const h = historyRef.current[activeStep];
    if (h.future.length === 0) return;

    const current = {
      playersOnCanvas,
      utilitiesOnCanvas,
      textBoxes,
      drawnLines,
      imagesOnCanvas,
      visionCones,
    };

    const next = h.future[h.future.length - 1];

    const isSame =
      JSON.stringify(current.playersOnCanvas) === JSON.stringify(next.playersOnCanvas) &&
      JSON.stringify(current.utilitiesOnCanvas) === JSON.stringify(next.utilitiesOnCanvas) &&
      JSON.stringify(current.textBoxes) === JSON.stringify(next.textBoxes) &&
      JSON.stringify(current.drawnLines) === JSON.stringify(next.drawnLines) &&
      JSON.stringify(current.imagesOnCanvas) === JSON.stringify(next.imagesOnCanvas) &&
      JSON.stringify(current.visionCones) === JSON.stringify(next.visionCones);

    if (isSame) return;

    suspendHistoryRef.current = true;

    h.past.push(current);
    const popped = h.future.pop();

    if (popped) {
      setPlayersOnCanvas(popped.playersOnCanvas);
      setUtilitiesOnCanvas(popped.utilitiesOnCanvas);
      setTextBoxes(popped.textBoxes);
      setDrawnLines(popped.drawnLines);
      setImagesOnCanvas(popped.imagesOnCanvas);
      setVisionCones(popped.visionCones || []);
    }

    setTimeout(() => {
      suspendHistoryRef.current = false;
    }, 50);
  };

  const saveAllSequencesToFirestore = async (formData, mode) => {
    const user = auth?.currentUser;
    if (!user) {
      console.error('❌ User not authenticated');
      return false;
    }

    const userDoc = await getDoc(doc(db, `users/${user.uid}`));
    const userData = userDoc.data();
    const baseTeamId = userData?.teamId;

    if (!baseTeamId) {
      console.error('❌ No teamId found for user');
      return false;
    }

    const { basePath, seasonId } = await resolveStrategyBasePathForWrite(baseTeamId);

    const newStrategyId = `strategy_${Date.now()}`;

    try {
      const strategyRef = doc(db, `${basePath}/${newStrategyId}`);

      const payload = {
        strategyId: newStrategyId,
        name: formData.title,
        map: mapName,
        mode,
        createdBy: {
          uid: user.uid,
          ign: userData?.ign || 'Unknown'
        },
        tags: formData.tags || {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isFavorite: false,
        teamId: baseTeamId,
        seasonId: seasonId || null
      };

      await setDoc(strategyRef, payload);

      const stepWrites = Object.entries(scenarioData).map(async ([step, data]) => {
        const stepRef = doc(db, `${basePath}/${newStrategyId}/sequences/${step}`);
        const safePlayers = sanitizeForFirestore(data.playersOnCanvas || []);
        const safeUtilities = sanitizeForFirestore(data.utilitiesOnCanvas || []);
        const safeDrawings = sanitizeForFirestore(data.drawnLines || []);
        const safeText = sanitizeForFirestore(data.textBoxes || []);
        const safeImages = sanitizeForFirestore(data.imagesOnCanvas || []);
        const safeCones = sanitizeForFirestore(data.visionCones || []);
        await setDoc(stepRef, {
          stepNumber: Number(step),
          playerPositions: safePlayers,
          utilityPlacements: safeUtilities,
          drawings: safeDrawings,
          textBoxes: safeText,
          imagesOnCanvas: safeImages,
          visionCones: safeCones,
          updatedBy: user.uid,
          updatedAt: serverTimestamp()
        });
      });

      await Promise.all(stepWrites);
      return true;
    } catch (err) {
      console.error('🔥 Failed to save to Firestore:', err);
      return false;
    }
  };

  const handleUpdateStrategy = async () => {
    if (!editingStrategyId) return;

    const user = auth?.currentUser;
    if (!user) return;

    const userRef = doc(db, `users/${user.uid}`);
    const userSnap = await getDoc(userRef);
    const teamId = userSnap.data()?.teamId;
    if (!teamId) return;

    const legacyBasePath = getLegacyStrategyBasePath(teamId);
    const resolved = await resolveStrategyBasePathForWrite(teamId);
    let basePath = resolved.basePath;
    let isLegacy = resolved.isLegacy;

    if (strategyBasePathRef.current?.basePath) {
      if (!strategyBasePathRef.current.isLegacy) {
        basePath = strategyBasePathRef.current.basePath;
        isLegacy = false;
      } else if (!resolved.seasonId) {
        basePath = strategyBasePathRef.current.basePath;
        isLegacy = true;
      } else {
        basePath = resolved.basePath;
        isLegacy = basePath === legacyBasePath;
      }
    }

    strategyBasePathRef.current = {
      basePath,
      seasonId: resolved.seasonId,
      isLegacy,
    };

    const stratRef = doc(db, `${basePath}/${editingStrategyId}`);

    await setDoc(stratRef, {
      updatedAt: serverTimestamp(),
      map: mapName,
      mode,
      teamId,
      seasonId: resolved.seasonId || null,
      lastEditedBy: user.uid
    }, { merge: true });

    const stepWrites = Object.entries(scenarioData).map(([stepNum, stepData]) => {
      const stepRef = doc(db, `${basePath}/${editingStrategyId}/sequences/${stepNum}`);
      const safePlayers = sanitizeForFirestore(stepData.playersOnCanvas || []);
      const safeUtilities = sanitizeForFirestore(stepData.utilitiesOnCanvas || []);
      const safeDrawings = sanitizeForFirestore(stepData.drawnLines || []);
      const safeText = sanitizeForFirestore(stepData.textBoxes || []);
      const safeImages = sanitizeForFirestore(stepData.imagesOnCanvas || []);
      const safeCones = sanitizeForFirestore(stepData.visionCones || []);
      return setDoc(stepRef, {
        stepNumber: Number(stepNum),
        playerPositions: safePlayers,
        utilityPlacements: safeUtilities,
        drawings: safeDrawings,
        textBoxes: safeText,
        imagesOnCanvas: safeImages,
        visionCones: safeCones,
        updatedBy: user.uid,
        updatedAt: serverTimestamp()
      });
    });

    await Promise.all(stepWrites);

    toast.success(`✅ "${strategyTitle}" updated successfully!`, {
      className: 'thebox-toastUpdate'
    });
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setIsMapCollapsed(true);
  };

  const updateTextBox = (id, updates) => {
    setTextBoxes(prev =>
      prev.map(tb =>
        tb.id === id ? { ...tb, ...updates } : tb
      )
    );
  };

  const initResize = (e, side, id) => {
    const isTouchEvent = !!(e?.touches || e?.changedTouches);
    if (!isTouchEvent) {
      e.preventDefault();
    }
    e.stopPropagation?.();
    const startPoint = getClientPoint(e);
    if (!startPoint) return;
    resizingRef.current = { id, side };

    const startX = startPoint.clientX;
    const startY = startPoint.clientY;

    setTextBoxes(prev => {
      const tb = prev.find(tb => tb.id === id);
      resizingRef.current.startWidth = tb?.width || 140;
      resizingRef.current.startHeight = tb?.height || 100;
      return prev;
    });

    const onPointerMove = (moveEvent) => {
      const movePoint = getClientPoint(moveEvent);
      if (!movePoint) return;
      if (moveEvent.cancelable) moveEvent.preventDefault();

      const deltaX = movePoint.clientX - startX;
      const deltaY = movePoint.clientY - startY;

      setTextBoxes(prev =>
        prev.map(tb => {
          if (tb.id !== id) return tb;

          let newWidth = tb.width;
          let newHeight = tb.height;

          if (side === 'left') {
            newWidth = resizingRef.current.startWidth - deltaX;
          } else if (side === 'right') {
            newWidth = resizingRef.current.startWidth + deltaX;
          }

          if (side === 'bottom') {
            newHeight = resizingRef.current.startHeight + deltaY;
          }

          return {
            ...tb,
            width: Math.max(60, newWidth),
            height: Math.max(40, newHeight),
          };
        })
      );
    };

    const onPointerUp = () => {
      resizingRef.current = { id: null, side: null };
      document.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('mouseup', onPointerUp);
      document.removeEventListener('touchmove', onPointerMove);
      document.removeEventListener('touchend', onPointerUp);
      document.removeEventListener('touchcancel', onPointerUp);
    };

    document.addEventListener('mousemove', onPointerMove);
    document.addEventListener('mouseup', onPointerUp);
    document.addEventListener('touchmove', onPointerMove, { passive: false });
    document.addEventListener('touchend', onPointerUp);
    document.addEventListener('touchcancel', onPointerUp);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const canvas = document.querySelector('.map-canvas');
      const canvasRect = canvas.getBoundingClientRect();
      const scrollTop = canvas.scrollTop;
      const scrollLeft = canvas.scrollLeft;
      const centerX = canvasRect.width / 2 + scrollLeft;
      const centerY = canvasRect.height / 2 + scrollTop;
      const newImage = {
        id: generateUniqueId(),
        src: reader.result,
        x: centerX - 75,
        y: centerY - 75,
        width: 150,
        height: 150,
      };
      setImagesOnCanvas(prev => [...prev, newImage]);
    };
    reader.readAsDataURL(file);
  };

  const observeTextboxResize = (element, id) => {
    if (resizeObservers.current[id]) return;

    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setTextBoxes(prev =>
          prev.map(tb =>
            tb.id === id ? { ...tb, width, height } : tb
          )
        );
      }
    });

    observer.observe(element);
    resizeObservers.current[id] = observer;
  };

  const clearEverything = () => {
    setTextBoxes([]);
    setPlayersOnCanvas([]);
    setUtilitiesOnCanvas([]);
    setImagesOnCanvas([]);
    setDrawnLines([]);
    setVisionCones([]);
  };

  const deletePlayers = () => setPlayersOnCanvas([]);
  const deleteUtilities = () => setUtilitiesOnCanvas([]);
  const deleteTextBoxes = () => setTextBoxes([]);
  const deleteDrawings = () => setDrawnLines([]);

  const deleteCurrentSequenceStep = () => {
    setPlayersOnCanvas([]);
    setUtilitiesOnCanvas([]);
    setTextBoxes([]);
    setImagesOnCanvas([]);
    setDrawnLines([]);
    setVisionCones([]);

    setScenarioData(prev => ({
      ...prev,
      [activeStep]: {
        stepId: activeStep,
        playersOnCanvas: [],
        utilitiesOnCanvas: [],
        textBoxes: [],
        drawnLines: [],
        imagesOnCanvas: [],
        visionCones: [],
      }
    }));
  };

  const getMousePosition = (e, svg) => {
    const CTM = svg.getScreenCTM();
    return {
      x: (e.clientX - CTM.e) / CTM.a,
      y: (e.clientY - CTM.f) / CTM.d,
    };
  };

  const eraseNearbyLines = (pt) => {
    setDrawnLines((prevLines) =>
      prevLines.filter((line) =>
        !line.points.some((p) => {
          const dx = pt.x - p.x;
          const dy = pt.y - p.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          return distance < 10;
        })
      )
    );

    if (pointsRef.current.length > 0) {
      const shouldEraseLive = pointsRef.current.some((p) => {
        const dx = pt.x - p.x;
        const dy = pt.y - p.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return distance < 10;
      });

      if (shouldEraseLive) {
        pointsRef.current = [];
        if (pathRef.current) {
          pathRef.current.setAttribute('d', '');
        }
        setIsDrawing(false);
      }
    }
  };

  const handleMouseLeave = () => {
    if (isDrawing) {
      pointsRef.current = [];
      setIsDrawing(false);
      pathRef.current?.setAttribute('d', '');
    }
    if (isErasing) {
      setIsErasing(false);
    }
  };

  const handleUIHover = () => {
    const penHovered = penPanelRef.current?.matches(':hover');
    if (!penHovered && activeTool !== 'pen') {
      setActiveTool(null);
    }
  };

  const toggleTool = (tool, coneTypeOverride = null) => {
    if (tool === 'visionCone') {
      const coneDefaults = {
        narrow: { fov: 30, radius: 100, color: '#ffffff' },
        medium: { fov: 60, radius: 120, color: '#ffffff' },
        wide: { fov: 120, radius: 140, color: '#ffffff' },
      };
      const typeToUse = coneTypeOverride || selectedConeType;
      const cfg = coneDefaults[typeToUse] || coneDefaults.medium;
      const origin = { x: 500, y: 300 };
      const target = { x: origin.x + cfg.radius * 0.5, y: origin.y };
      const newCone = {
        id: generateUniqueId(),
        type: typeToUse,
        origin,
        target,
        fov: cfg.fov,
        radius: cfg.radius,
        color: cfg.color,
      };
      setVisionCones(prev => [...prev, newCone]);
      setActiveTool(null);
      setIsPenPanelVisible(false);
      return;
    }

    setActiveTool(prev => {
      const isTogglingOff = prev === tool;

      if (tool === 'pen') {
        setIsPenPanelVisible(!isTogglingOff);
      }

      if (!isTogglingOff && tool !== 'pen') {
        setLastTool(tool);
      }

      return isTogglingOff ? null : tool;
    });

    if (tool !== 'pen') {
      setIsPenPanelVisible(false);
    }

    if (tool === 'text') {
      setTimeout(() => {
        const canvas = document.querySelector('.map-canvas');
        const canvasRect = canvas.getBoundingClientRect();
        const scrollTop = canvas.scrollTop;
        const scrollLeft = canvas.scrollLeft;
        const centerX = canvasRect.width / 2 + scrollLeft;
        const centerY = canvasRect.height / 2 + scrollTop;

        const relX = ((centerX - 70) / canvasRect.width) * 100;
        const relY = ((centerY - 20) / canvasRect.height) * 100;

        const newText = {
          id: generateUniqueId(),
          relX,
          relY,
          text: '',
          width: 140,
          height: 100
        };

        setTextBoxes(prev => [...prev, newText]);
      }, 0);
    }

    if (tool === 'image') {
      setTimeout(() => {
        document.getElementById('imageUploadInput')?.click();
      }, 0);
    }

    const utilityTypes = ['empgrenade', 'flash', 'smoke', 'trophy', 'grenade', 'sndbomb', 'warning', 'anchor', 'flag', 'star'];

    if (utilityTypes.includes(tool)) {
      setTimeout(() => {
        const canvas = document.querySelector('.map-canvas');
        const canvasRect = canvas.getBoundingClientRect();
        const scrollTop = canvas.scrollTop;
        const scrollLeft = canvas.scrollLeft;

        const grenadeTypes = ['empgrenade', 'flash', 'smoke', 'trophy', 'grenade'];
        const grenades = utilitiesOnCanvas.filter(u => grenadeTypes.includes(u.type));

        const grenadeCount = grenades.length;
        const columnUtil = grenadeCount % 5;
        const rowUtil = Math.floor(grenadeCount / 5);
        const relX = 60 + columnUtil * 5;
        const relY = 5 + rowUtil * 7;

        const newUtility = {
          id: generateUniqueId(),
          type: tool,
          relX,
          relY,
          src: ['warning', 'anchor', 'flag', 'star'].includes(tool)
            ? `/assets/tools/${tool.toUpperCase()}.png`
            : undefined,
        };

        setUtilitiesOnCanvas(prev => [...prev, newUtility]);

        if (activeTool === 'pen' || activeTool === 'eraser') {
          setActiveTool(null);
          setIsPenPanelVisible(false);
        }
      }, 0);
    }
  };

  const penTypes = [
    { id: 'line', label: '─' },
    { id: 'dotted', label: '┄' },
    { id: 'arrow', label: '→' },
    { id: 'dotted-arrow', label: '⇢' },
    { id: 'square', label: '■' },
  ];
  const colors = ['#ffffff', '#000000', '#ff0000', '#ffff00', '#00ff00', '#0066ff'];

  const handleMouseDown = (e) => {
    const svg = e.target.closest('svg');
    const pt = getMousePosition(e, svg);

    if (activeTool === 'eraser') {
      eraseNearbyLines(pt);
      setIsErasing(true);
      return;
    }

    if (activeTool !== 'pen') return;

    pointsRef.current = [pt];
    setIsDrawing(true);

    if (pathRef.current) {
      pathRef.current.setAttribute('d', `M ${pt.x} ${pt.y}`);
      pathRef.current.style.stroke = penSettings.color;
      pathRef.current.style.strokeWidth = penSettings.thickness;
      pathRef.current.style.opacity = penSettings.opacity / 10;
      pathRef.current.style.strokeLinecap = "round";
      pathRef.current.style.strokeLinejoin = "round";
      pathRef.current.style.fill = "none";
    }
  };

  const handleMouseMove = (e) => {
    const svg = e.currentTarget;
    const pt = getMousePosition(e, svg);

    if (cursorRef.current) {
      cursorRef.current.setAttribute('x', pt.x);
      cursorRef.current.setAttribute('y', pt.y);
    }

    setCursorPos({ x: pt.x, y: pt.y });

    if (activeTool === 'eraser' && isErasing) {
      const now = Date.now();
      if (now - eraseThrottleRef.current > 10) {
        eraseThrottleRef.current = now;

        const last = lastEraserPointRef.current;
        const steps = 5;
        const interpolatedPoints = [];

        if (last) {
          for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            interpolatedPoints.push({
              x: last.x + (pt.x - last.x) * t,
              y: last.y + (pt.y - last.y) * t,
            });
          }
        } else {
          interpolatedPoints.push(pt);
        }

        setDrawnLines((prevLines) =>
          prevLines.filter((line) =>
            !line.points.some((p) =>
              interpolatedPoints.some((ip) => {
                const dx = ip.x - p.x;
                const dy = ip.y - p.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                return distance < 15;
              })
            )
          )
        );

        lastEraserPointRef.current = pt;
      }
      return;
    }

    if (!isDrawing || activeTool !== 'pen') return;

    pointsRef.current.push(pt);

    const pathD = pointsRef.current.map((p, i) =>
      i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`
    ).join(' ');

    if (pathRef.current) {
      pathRef.current.setAttribute('d', pathD);
    }
  };

  const handleMouseUp = () => {
    if (isErasing) {
      setIsErasing(false);
      return;
    }

    if (!isDrawing || pointsRef.current.length === 0) return;

    const newLine = {
      points: [...pointsRef.current],
      color: penSettings.color,
      thickness: penSettings.thickness,
      opacity: penSettings.opacity,
      type: penSettings.type
    };

    setDrawnLines(prev => [...prev, newLine]);

    dragJustEndedRef.current = true;

    setScenarioData(prev => {
      const existingStep = prev[activeStep] || {};
      return {
        ...prev,
        [activeStep]: {
          ...existingStep,
          stepId: activeStep,
          playersOnCanvas,
          utilitiesOnCanvas,
          textBoxes,
          imagesOnCanvas,
          visionCones,
          drawnLines: [...(existingStep.drawnLines || []), newLine],
        }
      };
    });

    if (pathRef.current) {
      pathRef.current.setAttribute('d', '');
    }

    pointsRef.current = [];
    setIsDrawing(false);
  };

  const handleCanvasClick = (e) => {
    const svg = e.target.closest('svg');
    const pt = getMousePosition(e, svg);

    if (activeTool === 'eraser') {
      setDrawnLines((prevLines) =>
        prevLines.filter((line) => {
          return !line.points.some((p) => {
            const dx = pt.x - p.x;
            const dy = pt.y - p.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            return distance < 15;
          });
        })
      );
      return;
    }
  };

  // ============================================
  // CANVAS HANDLERS (wrappers for component)
  // ============================================
  
  const onCanvasClick = () => {
    setSelectedObject(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;

    const player = JSON.parse(data);
    const canvasRect = e.currentTarget.getBoundingClientRect();

    const relX = ((e.clientX - canvasRect.left - 25) / canvasRect.width) * 100;
    const relY = ((e.clientY - canvasRect.top - 25) / canvasRect.height) * 100;

    const newPlayer = {
      id: generateUniqueId(),
      name: player.name,
      avatar: player.avatar,
      relX,
      relY,
      side: player.side || teamAssetsSide || 'ally',
      sourcePackId: player.sourcePackId,
      sourceTeam: player.sourceTeam,
      sourceTeamName: player.sourceTeamName,
      sourcePlayerId: player.sourcePlayerId,
      sourcePlayerIdentity: player.sourcePlayerIdentity,
    };

    setPlayersOnCanvas(prev => [...prev, newPlayer]);
    setActiveTool(null);
    setIsPenPanelVisible(false);
  };

  const onPlayerClick = (player) => {
    if (
      selectedObject?.type === 'player' &&
      selectedObject?.id === player.id
    ) {
      setSelectedObject(null);
    } else {
      setSelectedObject({ type: 'player', id: player.id });
    }
  };

  const onPlayerMouseDown = (e, player) => {
    e.stopPropagation();
    const point = getClientPoint(e);
    if (!point) return;

    const h = historyRef.current[activeStep];
    const snapshot = {
      playersOnCanvas: JSON.parse(JSON.stringify(playersOnCanvas)),
      utilitiesOnCanvas: JSON.parse(JSON.stringify(utilitiesOnCanvas)),
      textBoxes: JSON.parse(JSON.stringify(textBoxes)),
      drawnLines: JSON.parse(JSON.stringify(drawnLines)),
      imagesOnCanvas: JSON.parse(JSON.stringify(imagesOnCanvas)),
      visionCones: JSON.parse(JSON.stringify(visionCones)),
    };
    const last = h.past[h.past.length - 1];
    const isDuplicate = last && JSON.stringify(last) === JSON.stringify(snapshot);
    if (!isDuplicate) {
      h.past.push(snapshot);
      h.future = [];
    }

    const offsetX = point.clientX - (canvasSize.current.left + (player.relX / 100) * canvasSize.current.width);
    const offsetY = point.clientY - (canvasSize.current.top + (player.relY / 100) * canvasSize.current.height);
    dragOffset.current = { x: offsetX, y: offsetY };
    setDraggingPlayerId(player.id);
  };

  const onUtilityClick = (util) => {
    if (
      selectedObject?.type === 'utility' &&
      selectedObject?.id === util.id
    ) {
      setSelectedObject(null);
    } else {
      setSelectedObject({ type: 'utility', id: util.id });
    }
  };

  const onUtilityMouseDown = (e, util) => {
    if (activeTool === 'pen' || activeTool === 'eraser') return;
    const point = getClientPoint(e);
    if (!point) return;

    const h = historyRef.current[activeStep];
    const snapshot = {
      playersOnCanvas,
      utilitiesOnCanvas,
      textBoxes,
      drawnLines,
      imagesOnCanvas,
      visionCones,
    };
    const last = h.past[h.past.length - 1];
    const isDuplicate = last && JSON.stringify(last) === JSON.stringify(snapshot);
    if (!isDuplicate) {
      h.past.push(snapshot);
      h.future = [];
    }

    const offsetX = point.clientX - (canvasSize.current.left + (util.relX / 100) * canvasSize.current.width);
    const offsetY = point.clientY - (canvasSize.current.top + (util.relY / 100) * canvasSize.current.height);
    dragOffset.current = { x: offsetX, y: offsetY };
    setDraggingUtilityId(util.id);
  };

  const onTextBoxClick = (tb) => {
    setSelectedObject({ type: 'text', id: tb.id });
  };

  const onTextBoxMouseDown = (e, tb) => {
    if (e.target.classList.contains('resize-handle')) return;
    const point = getClientPoint(e);
    if (!point) return;

    const h = historyRef.current[activeStep];
    const snapshot = {
      playersOnCanvas,
      utilitiesOnCanvas,
      textBoxes,
      drawnLines,
      imagesOnCanvas,
      visionCones,
    };
    const last = h.past[h.past.length - 1];
    const isDuplicate = last && JSON.stringify(last) === JSON.stringify(snapshot);
    if (!isDuplicate) {
      h.past.push(snapshot);
      h.future = [];
    }

    const offsetX = point.clientX - (canvasSize.current.left + (tb.relX / 100) * canvasSize.current.width);
    const offsetY = point.clientY - (canvasSize.current.top + (tb.relY / 100) * canvasSize.current.height);
    dragOffset.current = { x: offsetX, y: offsetY };
    setDraggingTextId(tb.id);
  };

  const onTextBoxInput = (id, text, height) => {
    updateTextBox(id, {
      text,
      height
    });
  };

  const onImageClick = (img) => {
    if (selectedObject?.type === 'image' && selectedObject?.id === img.id) {
      setSelectedObject(null);
    } else {
      setSelectedObject({ type: 'image', id: img.id });
    }
  };

  const onImageMouseDown = (e, img) => {
    e.stopPropagation();
    const point = getClientPoint(e);
    if (!point) return;

    const h = historyRef.current[activeStep];
    const snapshot = {
      playersOnCanvas: JSON.parse(JSON.stringify(playersOnCanvas)),
      utilitiesOnCanvas: JSON.parse(JSON.stringify(utilitiesOnCanvas)),
      textBoxes: JSON.parse(JSON.stringify(textBoxes)),
      drawnLines: JSON.parse(JSON.stringify(drawnLines)),
      imagesOnCanvas: JSON.parse(JSON.stringify(imagesOnCanvas)),
      visionCones: JSON.parse(JSON.stringify(visionCones)),
    };
    const last = h.past[h.past.length - 1];
    const isDuplicate = last && JSON.stringify(last) === JSON.stringify(snapshot);
    if (!isDuplicate) {
      h.past.push(snapshot);
      h.future = [];
    }

    const imageX = img.relX !== undefined
      ? (img.relX / 100) * canvasSize.current.width
      : (img.x || 0);
    const imageY = img.relY !== undefined
      ? (img.relY / 100) * canvasSize.current.height
      : (img.y || 0);

    const offsetX = point.clientX - (canvasSize.current.left + imageX);
    const offsetY = point.clientY - (canvasSize.current.top + imageY);
    dragOffset.current = { x: offsetX, y: offsetY };
    setDraggingImageId(img.id);
  };

  const onConesChange = (cones) => {
    const h = historyRef.current[activeStep];
    const snapshot = {
      playersOnCanvas: JSON.parse(JSON.stringify(playersOnCanvas)),
      utilitiesOnCanvas: JSON.parse(JSON.stringify(utilitiesOnCanvas)),
      textBoxes: JSON.parse(JSON.stringify(textBoxes)),
      drawnLines: JSON.parse(JSON.stringify(drawnLines)),
      imagesOnCanvas: JSON.parse(JSON.stringify(imagesOnCanvas)),
      visionCones: JSON.parse(JSON.stringify(visionCones)),
    };
    const last = h.past[h.past.length - 1];
    const isDuplicate = last && JSON.stringify(last) === JSON.stringify(snapshot);
    if (!isDuplicate) {
      h.past.push(snapshot);
      h.future = [];
    }
    setVisionCones(cones);
  };

  const onMouseEnter = () => {
    setIsCursorInside(true);
  };

  // ============================================
  // RETURN
  // ============================================
  return {
    state: {
      showDropdown,
      isConfirmSaveModalOpen,
      communityAssetPacks,
      seasonAssets,
      seasonPlayers,
      customPlayers,
      assetsLoading,
      teamId,
      activeSeasonId,
      activeSeasonYear,
      teamAssetsExpanded,
      teamAssetsSide,
      hoveredPlayerMenu,
      strategyTitle,
      editingStrategyId,
      selectedObject,
      pendingMapName,
      isLoadedFromFirestore,
      loadedStepsLocked,
      stepSwitching,
      saveNotification,
      isSaveModalOpen,
      currentUser,
      userProfile,
      teamLogo,
      cursorPos,
      mode,
      mapName,
      isMapCollapsed,
      showAssignmentsOverlay,
      draggingUtilityId,
      isPenPanelVisible,
      activeStep,
      justLoaded,
      playersOnCanvas,
      utilitiesOnCanvas,
      textBoxes,
      drawnLines,
      imagesOnCanvas,
      visionCones,
      selectedConeType,
      canvasDimensions,
      draggingPlayerId,
      isErasing,
      draggingTextId,
      draggingImageId,
      activeTool,
      penSettings,
      lastTool,
      isDrawing,
      isCursorInside,
      expandedTeam,
      scenarioData,
      MODES,
      modeLabels,
      modeMaps,
      selectedMap,
      currentRoster,
      primaryIconStyle,
      secondaryIconStyle,
      penTypes,
      colors,
      assignmentRows,
      roleCounts,
    },
    actions: {
      setShowDropdown,
      setIsConfirmSaveModalOpen,
      setCustomPlayers,
      setAssetsLoading,
      setTeamId,
      setTeamAssetsExpanded,
      setTeamAssetsSide,
      setHoveredPlayerMenu,
      setStrategyTitle,
      setEditingStrategyId,
      setSelectedObject,
      setPendingMapName,
      setIsLoadedFromFirestore,
      setLoadedStepsLocked,
      setStepSwitching,
      setSaveNotification,
      setIsSaveModalOpen,
      setCurrentUser,
      setUserProfile,
      setTeamLogo,
      setCursorPos,
      setMode,
      setMapName,
      setIsMapCollapsed,
      setShowAssignmentsOverlay,
      setDraggingUtilityId,
      setIsPenPanelVisible,
      setActiveStep,
      setJustLoaded,
      setPlayersOnCanvas,
      setUtilitiesOnCanvas,
      setTextBoxes,
      setDrawnLines,
      setImagesOnCanvas,
      setVisionCones,
      setSelectedConeType,
      setCanvasDimensions,
      setDraggingPlayerId,
      setIsErasing,
      setDraggingTextId,
      setDraggingImageId,
      setActiveTool,
      setPenSettings,
      setLastTool,
      setIsDrawing,
      setIsCursorInside,
      setExpandedTeam,
      setScenarioData,
      undo,
      redo,
      saveAllSequencesToFirestore,
      handleUpdateStrategy,
      switchMode,
      updateTextBox,
      initResize,
      handleImageUpload,
      observeTextboxResize,
      clearEverything,
      deletePlayers,
      deleteUtilities,
      deleteTextBoxes,
      deleteDrawings,
      deleteCurrentSequenceStep,
      getMousePosition,
      eraseNearbyLines,
      handleMouseLeave,
      handleUIHover,
      toggleTool,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleCanvasClick,
      getPlayerNameByAvatar,
      generateUniqueId,
      updateAssignment,
      handleAssignDragStart,
      // Step switching
      handleStepSwitch,
      // Canvas handlers
      onCanvasClick,
      onDrop,
      onPlayerClick,
      onPlayerMouseDown,
      onUtilityClick,
      onUtilityMouseDown,
      onTextBoxClick,
      onTextBoxMouseDown,
      onTextBoxInput,
      onImageClick,
      onImageMouseDown,
      onConesChange,
      onMouseEnter,
    },
    refs: {
      canvasLoadedRef,
      hoverMenuTimeout,
      suspendHistoryRef,
      dragJustEndedRef,
      historyRef,
      resizeObservers,
      penPanelRef,
      canvasSize,
      dragOffset,
      cursorRef,
      pathRef,
      pointsRef,
      eraseThrottleRef,
      lastEraserPointRef,
      resizingRef,
      assignPanelPos,
    },
  };
};
